import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { DEFAULT_CONTENT } from "../mock";
import { useSiteMeta } from "../context/SiteContext";
import { LogOut, Save, ChevronDown, ChevronRight, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "df_admin_token";

// ---------- tiny field helpers ----------
const Text = ({ label, value, onChange, placeholder }) => (
  <label className="block mb-3">
    <span className="block text-[10px] uppercase tracking-[0.22em] text-stone-400 mb-1">{label}</span>
    <input
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-[#141414] border border-zinc-800 focus:border-amber-300 outline-none px-3 py-2 text-stone-100 text-sm rounded-sm"
    />
  </label>
);

const Area = ({ label, value, onChange, rows = 3, placeholder }) => (
  <label className="block mb-3">
    <span className="block text-[10px] uppercase tracking-[0.22em] text-stone-400 mb-1">{label}</span>
    <textarea
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      placeholder={placeholder}
      className="w-full bg-[#141414] border border-zinc-800 focus:border-amber-300 outline-none px-3 py-2 text-stone-100 text-sm rounded-sm leading-relaxed"
    />
  </label>
);

// Edits an array of strings as a textarea (one item per line)
const Lines = ({ label, value, onChange, rows = 5, placeholder }) => {
  const text = (value || []).join("\n");
  return (
    <Area
      label={label}
      value={text}
      rows={rows}
      placeholder={placeholder}
      onChange={(v) => onChange(v.split("\n").map((s) => s).filter((s) => s.length > 0))}
    />
  );
};

// Edits array of {label, href}
const LinksEditor = ({ value, onChange }) => {
  const items = value || [];
  const update = (idx, key, v) => {
    const next = items.map((it, i) => (i === idx ? { ...it, [key]: v } : it));
    onChange(next);
  };
  const add = () => onChange([...items, { label: "", href: "" }]);
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div className="mb-3">
      <span className="block text-[10px] uppercase tracking-[0.22em] text-stone-400 mb-2">Press &amp; Links</span>
      {items.map((it, i) => (
        <div key={i} className="flex gap-2 mb-2">
          <input
            value={it.label || ""}
            onChange={(e) => update(i, "label", e.target.value)}
            placeholder="Label"
            className="flex-1 bg-[#141414] border border-zinc-800 px-2 py-1.5 text-xs text-stone-100 rounded-sm"
          />
          <input
            value={it.href || ""}
            onChange={(e) => update(i, "href", e.target.value)}
            placeholder="https://…"
            className="flex-1 bg-[#141414] border border-zinc-800 px-2 py-1.5 text-xs text-stone-100 rounded-sm"
          />
          <button
            type="button"
            onClick={() => remove(i)}
            className="px-2 py-1 text-xs text-stone-400 border border-zinc-800 hover:border-red-500 hover:text-red-400 rounded-sm"
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="text-xs text-amber-300 hover:text-amber-200 border border-amber-400/30 px-3 py-1.5 rounded-sm"
      >
        + Add link
      </button>
    </div>
  );
};

const Section = ({ title, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-zinc-800 mb-4">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 bg-[#111] hover:bg-[#161616]"
      >
        <span className="font-display text-lg text-stone-50">{title}</span>
        {open ? <ChevronDown size={16} className="text-stone-400" /> : <ChevronRight size={16} className="text-stone-400" />}
      </button>
      {open && <div className="p-5 bg-[#0c0c0c]">{children}</div>}
    </div>
  );
};

// ---------- Login ----------
const Login = ({ onLogin }) => {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password: pw });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6">
      <form onSubmit={submit} className="w-full max-w-sm border border-zinc-800 p-8 bg-[#0f0f0f]">
        <div className="flex items-center gap-3 mb-6 text-amber-300">
          <Lock size={16} />
          <span className="font-mono text-[11px] uppercase tracking-[0.22em]">Admin — Sign in</span>
        </div>
        <h1 className="font-display text-3xl text-stone-50 mb-6">Edit content</h1>
        <Text label="Password" value={pw} onChange={setPw} placeholder="••••••••" />
        {err && <p className="text-red-400 text-xs mb-3">{err}</p>}
        <button
          type="submit"
          disabled={loading || !pw}
          className="w-full bg-stone-50 text-zinc-950 py-3 uppercase tracking-[0.2em] text-[11px] font-medium hover:bg-amber-300 transition-colors disabled:opacity-60"
        >
          {loading ? "Signing in…" : "Sign in"}
        </button>
        <p className="mt-6 text-[11px] text-stone-500">
          Editing here updates the live site immediately. Use it sparingly.
        </p>
      </form>
    </div>
  );
};

// ---------- Editor ----------
const Editor = ({ token, onLogout }) => {
  const { site, refresh } = useSiteMeta();
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    // Use whatever is in context as the starting point (merged already)
    setContent(JSON.parse(JSON.stringify(site)));
  }, [site]);

  const update = (path, value) => {
    setContent((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        if (cur[k] === undefined || cur[k] === null) cur[k] = {};
        cur = cur[k];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const updateArrItem = (arr, idx, key, value) => {
    const next = (content[arr] || []).map((it, i) => (i === idx ? { ...it, [key]: value } : it));
    update(arr, next);
  };

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      await axios.put(
        `${API}/content`,
        { data: content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSavedAt(new Date());
      refresh();
    } catch (e) {
      if (e?.response?.status === 401) {
        onLogout();
        return;
      }
      setErr(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    if (window.confirm("Reset ALL content back to the original defaults? (Your edits in the form will be cleared — click Save after to persist)")) {
      setContent(JSON.parse(JSON.stringify(DEFAULT_CONTENT)));
    }
  };

  const statusLine = useMemo(() => {
    if (saving) return "Saving…";
    if (err) return err;
    if (savedAt) return `Saved ${savedAt.toLocaleTimeString()}`;
    return "";
  }, [saving, err, savedAt]);

  if (!content) return null;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-stone-100 pb-32">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-[#0a0a0a]/90 backdrop-blur-md border-b border-zinc-900">
        <div className="max-w-5xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
          <div>
            <span className="font-display text-lg">Daniel.Flynn</span>
            <span className="ml-3 text-[10px] uppercase tracking-[0.22em] text-amber-300">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-stone-400">{statusLine}</span>
            <a href="/" target="_blank" rel="noreferrer" className="text-xs text-stone-400 hover:text-amber-300">
              View site ↗
            </a>
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-stone-50 text-zinc-950 px-4 py-2 uppercase tracking-[0.18em] text-[10px] font-medium hover:bg-amber-300 transition-colors disabled:opacity-60"
            >
              <Save size={13} /> Save
            </button>
            <button onClick={onLogout} className="text-stone-400 hover:text-red-400" title="Sign out">
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-8 py-10">
        <h1 className="font-display text-4xl md:text-5xl text-stone-50 mb-2">Edit the site</h1>
        <p className="text-stone-500 text-sm mb-8">
          All changes go live immediately on save. Fields that aren't edited keep their previous value.
        </p>

        {/* Profile */}
        <Section title="Profile &amp; Hero" defaultOpen>
          <div className="grid md:grid-cols-2 gap-4">
            <Text label="First name" value={content.profile?.firstName} onChange={(v) => update("profile.firstName", v)} />
            <Text label="Last name" value={content.profile?.lastName} onChange={(v) => update("profile.lastName", v)} />
            <Text label="Full name" value={content.profile?.name} onChange={(v) => update("profile.name", v)} />
            <Text label="Title" value={content.profile?.title} onChange={(v) => update("profile.title", v)} />
            <Text label="Tagline" value={content.profile?.tagline} onChange={(v) => update("profile.tagline", v)} />
            <Text label="Location" value={content.profile?.location} onChange={(v) => update("profile.location", v)} />
            <Text label="Origin" value={content.profile?.origin} onChange={(v) => update("profile.origin", v)} />
            <Text label="Email" value={content.profile?.email} onChange={(v) => update("profile.email", v)} />
            <Text label="Phone" value={content.profile?.phone} onChange={(v) => update("profile.phone", v)} />
            <Text label="CV URL (PDF)" value={content.profile?.cvUrl} onChange={(v) => update("profile.cvUrl", v)} />
            <Text label="LinkedIn" value={content.profile?.socials?.linkedin} onChange={(v) => update("profile.socials.linkedin", v)} />
            <Text label="YouTube" value={content.profile?.socials?.youtube} onChange={(v) => update("profile.socials.youtube", v)} />
            <Text label="Legacy site" value={content.profile?.socials?.website} onChange={(v) => update("profile.socials.website", v)} />
            <Text label="Calendly" value={content.profile?.socials?.calendly} onChange={(v) => update("profile.socials.calendly", v)} />
          </div>
          <Area label="Short intro" value={content.profile?.intro} onChange={(v) => update("profile.intro", v)} rows={3} />
          <Lines
            label="Long bio (one paragraph per line)"
            value={content.profile?.longBio}
            onChange={(v) => update("profile.longBio", v)}
            rows={6}
          />

          <div className="mt-4 pt-4 border-t border-zinc-800">
            <p className="text-[10px] uppercase tracking-[0.22em] text-stone-400 mb-3">Stats (4 cards)</p>
            <div className="grid md:grid-cols-2 gap-3">
              {(content.profile?.stats || []).map((s, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    value={s.value || ""}
                    onChange={(e) => {
                      const next = [...content.profile.stats];
                      next[i] = { ...s, value: e.target.value };
                      update("profile.stats", next);
                    }}
                    placeholder="Value"
                    className="w-24 bg-[#141414] border border-zinc-800 px-2 py-1.5 text-xs text-stone-100 rounded-sm"
                  />
                  <input
                    value={s.label || ""}
                    onChange={(e) => {
                      const next = [...content.profile.stats];
                      next[i] = { ...s, label: e.target.value };
                      update("profile.stats", next);
                    }}
                    placeholder="Label"
                    className="flex-1 bg-[#141414] border border-zinc-800 px-2 py-1.5 text-xs text-stone-100 rounded-sm"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-zinc-800">
            <p className="text-[10px] uppercase tracking-[0.22em] text-stone-400 mb-3">Hero trailer</p>
            <div className="grid md:grid-cols-2 gap-4">
              <Text label="YouTube video ID" value={content.heroReel?.youtubeId} onChange={(v) => update("heroReel.youtubeId", v)} />
              <Text label="Title" value={content.heroReel?.title} onChange={(v) => update("heroReel.title", v)} />
              <Text label="Client (caption)" value={content.heroReel?.client} onChange={(v) => update("heroReel.client", v)} />
              <Text label="Role (caption)" value={content.heroReel?.role} onChange={(v) => update("heroReel.role", v)} />
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-zinc-800">
            <Lines
              label="Rolling client marquee (one name per line)"
              value={content.clientLogos}
              onChange={(v) => update("clientLogos", v)}
              rows={8}
            />
          </div>
        </Section>

        {/* Projects */}
        <Section title={`Projects (${content.projects?.length || 0})`}>
          {(content.projects || []).map((p, idx) => (
            <div key={p.slug || idx} className="border border-zinc-800 p-4 mb-4 bg-[#0a0a0a]">
              <p className="font-display text-xl text-stone-50 mb-3">
                {idx + 1}. {p.title}
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <Text label="Title" value={p.title} onChange={(v) => updateArrItem("projects", idx, "title", v)} />
                <Text label="Year" value={p.year} onChange={(v) => updateArrItem("projects", idx, "year", v)} />
                <Text label="Role" value={p.role} onChange={(v) => updateArrItem("projects", idx, "role", v)} />
                <Text label="Client" value={p.client} onChange={(v) => updateArrItem("projects", idx, "client", v)} />
                <Text label="Cover image URL" value={p.cover} onChange={(v) => updateArrItem("projects", idx, "cover", v)} />
                <Text label="Pull quote (optional)" value={p.pullQuote} onChange={(v) => updateArrItem("projects", idx, "pullQuote", v)} />
              </div>
              <Lines label="Categories (one per line)" value={p.categories} onChange={(v) => updateArrItem("projects", idx, "categories", v)} rows={3} />
              <Area label="Summary" value={p.summary} onChange={(v) => updateArrItem("projects", idx, "summary", v)} rows={4} />
              <Lines label="Outcomes (one bullet per line)" value={p.impact} onChange={(v) => updateArrItem("projects", idx, "impact", v)} rows={4} />
              <LinksEditor value={p.links} onChange={(v) => updateArrItem("projects", idx, "links", v)} />
              <p className="text-[10px] text-stone-500">
                Videos ({p.videos?.length || 0}) are managed in the code for now — tell Emergent if you want to add / remove one.
              </p>
            </div>
          ))}
        </Section>

        {/* Experience */}
        <Section title={`Experience (${content.experience?.length || 0})`}>
          {(content.experience || []).map((j, idx) => (
            <div key={idx} className="border border-zinc-800 p-4 mb-4 bg-[#0a0a0a]">
              <p className="font-display text-xl text-stone-50 mb-3">
                {j.role} — {j.company}
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <Text label="Company" value={j.company} onChange={(v) => updateArrItem("experience", idx, "company", v)} />
                <Text label="Role" value={j.role} onChange={(v) => updateArrItem("experience", idx, "role", v)} />
                <Text label="Location" value={j.location} onChange={(v) => updateArrItem("experience", idx, "location", v)} />
                <Text label="Period" value={j.period} onChange={(v) => updateArrItem("experience", idx, "period", v)} />
                <Text label="Team-size chip" value={j.teamSize} onChange={(v) => updateArrItem("experience", idx, "teamSize", v)} />
              </div>
              <Lines label="Bullets (one per line)" value={j.bullets} onChange={(v) => updateArrItem("experience", idx, "bullets", v)} rows={5} />
            </div>
          ))}
        </Section>

        {/* Capabilities */}
        <Section title="Capabilities, tools &amp; credentials">
          <p className="text-[10px] uppercase tracking-[0.22em] text-stone-400 mb-2">Skill groups</p>
          {Object.entries(content.skills || {}).map(([group, items]) => (
            <div key={group} className="mb-4">
              <p className="text-stone-300 text-sm mb-1">{group}</p>
              <Lines
                label=""
                value={items}
                onChange={(v) => update(`skills.${group}`, v)}
                rows={4}
              />
            </div>
          ))}

          <div className="mt-4 pt-4 border-t border-zinc-800">
            <p className="text-[10px] uppercase tracking-[0.22em] text-stone-400 mb-2">AI tools (tile grid)</p>
            {(content.aiTools || []).map((t, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input
                  value={t.letter || ""}
                  onChange={(e) => updateArrItem("aiTools", i, "letter", e.target.value)}
                  placeholder="L"
                  className="w-12 bg-[#141414] border border-zinc-800 px-2 py-1.5 text-xs text-stone-100 rounded-sm"
                />
                <input
                  value={t.name || ""}
                  onChange={(e) => updateArrItem("aiTools", i, "name", e.target.value)}
                  placeholder="Tool name"
                  className="flex-1 bg-[#141414] border border-zinc-800 px-2 py-1.5 text-xs text-stone-100 rounded-sm"
                />
              </div>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-zinc-800">
            <p className="text-[10px] uppercase tracking-[0.22em] text-stone-400 mb-2">Credentials / certifications</p>
            {(content.certifications || []).map((c, i) => (
              <div key={i} className="grid md:grid-cols-2 gap-2 mb-2">
                <input
                  value={c.title || ""}
                  onChange={(e) => updateArrItem("certifications", i, "title", e.target.value)}
                  placeholder="Title"
                  className="bg-[#141414] border border-zinc-800 px-2 py-1.5 text-xs text-stone-100 rounded-sm"
                />
                <input
                  value={c.org || ""}
                  onChange={(e) => updateArrItem("certifications", i, "org", e.target.value)}
                  placeholder="Issuer / detail"
                  className="bg-[#141414] border border-zinc-800 px-2 py-1.5 text-xs text-stone-100 rounded-sm"
                />
              </div>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-zinc-800">
            <p className="text-[10px] uppercase tracking-[0.22em] text-stone-400 mb-2">EU AI Act timeline</p>
            {(content.aiActTimeline || []).map((m, i) => (
              <div key={i} className="grid md:grid-cols-2 gap-2 mb-2">
                <input
                  value={m.date || ""}
                  onChange={(e) => updateArrItem("aiActTimeline", i, "date", e.target.value)}
                  placeholder="Date"
                  className="bg-[#141414] border border-zinc-800 px-2 py-1.5 text-xs text-stone-100 rounded-sm"
                />
                <input
                  value={m.label || ""}
                  onChange={(e) => updateArrItem("aiActTimeline", i, "label", e.target.value)}
                  placeholder="Milestone"
                  className="bg-[#141414] border border-zinc-800 px-2 py-1.5 text-xs text-stone-100 rounded-sm"
                />
              </div>
            ))}
          </div>
        </Section>

        {/* Education + Nav */}
        <Section title="Education &amp; navigation">
          {(content.education || []).map((e, i) => (
            <div key={i} className="grid md:grid-cols-2 gap-4 mb-4">
              <Text label="School" value={e.school} onChange={(v) => updateArrItem("education", i, "school", v)} />
              <Text label="Degree" value={e.degree} onChange={(v) => updateArrItem("education", i, "degree", v)} />
              <Text label="Period" value={e.period} onChange={(v) => updateArrItem("education", i, "period", v)} />
              <Area label="Note" value={e.note} onChange={(v) => updateArrItem("education", i, "note", v)} rows={2} />
            </div>
          ))}
          <div className="mt-4 pt-4 border-t border-zinc-800">
            <p className="text-[10px] uppercase tracking-[0.22em] text-stone-400 mb-2">Top navigation labels</p>
            {(content.nav || []).map((n, i) => (
              <div key={n.id} className="flex gap-2 mb-2 items-center">
                <span className="w-24 text-[10px] text-stone-500">{n.id}</span>
                <input
                  value={n.label || ""}
                  onChange={(e) => updateArrItem("nav", i, "label", e.target.value)}
                  className="flex-1 bg-[#141414] border border-zinc-800 px-2 py-1.5 text-xs text-stone-100 rounded-sm"
                />
              </div>
            ))}
          </div>
        </Section>

        <div className="mt-10 flex items-center justify-between">
          <button onClick={resetToDefaults} className="text-xs text-stone-400 hover:text-red-400 underline">
            Reset form to defaults
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-stone-50 text-zinc-950 px-6 py-3 uppercase tracking-[0.2em] text-[11px] font-medium hover:bg-amber-300 transition-colors disabled:opacity-60"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
        {err && <p className="mt-3 text-red-400 text-sm">{err}</p>}
      </main>
    </div>
  );
};

// ---------- Page entry ----------
const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  useEffect(() => {
    if (!token) return;
    // Validate the token with the backend; clear if invalid
    axios
      .get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      });
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <Login onLogin={(t) => setToken(t)} />;
  return <Editor token={token} onLogout={logout} />;
};

export default Admin;
